import InternalChatAPI from '../../api/internalChat';

export const state = {
  threads: [],
  messages: {},
  uiFlags: {
    isFetchingThreads: false,
    isFetchingMessages: false,
    isCreatingThread: false,
    isSendingMessage: false,
    isDeletingThread: false,
  },
};

const sortThreads = threads =>
  threads.sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));

const upsertThread = (threads, thread) => {
  const index = threads.findIndex(item => item.id === thread.id);
  if (index === -1) {
    threads.push(thread);
  } else {
    threads.splice(index, 1, { ...threads[index], ...thread });
  }
  sortThreads(threads);
};

export const getters = {
  getThreads($state) {
    return $state.threads;
  },
  getThreadById: $state => id => {
    return $state.threads.find(thread => thread.id === Number(id)) || null;
  },
  getMessagesByThread: $state => threadId => {
    return $state.messages[threadId] || [];
  },
  getUnreadCount($state) {
    return $state.threads.reduce(
      (count, thread) => count + Number(thread.unread_count || 0),
      0
    );
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  fetchThreads: async ({ commit }) => {
    commit('setUIFlag', { isFetchingThreads: true });
    try {
      const response = await InternalChatAPI.get();
      commit('setThreads', response.data);
    } finally {
      commit('setUIFlag', { isFetchingThreads: false });
    }
  },
  createThread: async ({ commit }, payload) => {
    commit('setUIFlag', { isCreatingThread: true });
    try {
      const response = await InternalChatAPI.createThread(payload);
      commit('upsertThread', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isCreatingThread: false });
    }
  },
  fetchMessages: async ({ commit }, { threadId, beforeId } = {}) => {
    commit('setUIFlag', { isFetchingMessages: true });
    try {
      const response = await InternalChatAPI.getMessages(threadId, beforeId);
      commit(beforeId ? 'prependMessages' : 'setMessages', {
        threadId,
        messages: response.data,
      });
      return response.data;
    } finally {
      commit('setUIFlag', { isFetchingMessages: false });
    }
  },
  sendMessage: async (
    { commit },
    { threadId, content, files = [], replyToMessageId = null }
  ) => {
    commit('setUIFlag', { isSendingMessage: true });
    try {
      const response = await InternalChatAPI.createMessage(threadId, {
        content,
        files,
        replyToMessageId,
      });
      commit('upsertThread', response.data.thread);
      commit('appendMessage', { threadId, message: response.data.message });
      return response.data;
    } finally {
      commit('setUIFlag', { isSendingMessage: false });
    }
  },
  updateMessage: async ({ commit }, { threadId, messageId, content }) => {
    commit('setUIFlag', { isSendingMessage: true });
    try {
      const response = await InternalChatAPI.updateMessage(
        threadId,
        messageId,
        content
      );
      commit('upsertThread', response.data.thread);
      commit('upsertMessage', { threadId, message: response.data.message });
      return response.data;
    } finally {
      commit('setUIFlag', { isSendingMessage: false });
    }
  },
  markRead: async ({ commit }, threadId) => {
    const response = await InternalChatAPI.markRead(threadId);
    commit('upsertThread', response.data);
  },
  deleteThread: async ({ commit }, threadId) => {
    commit('setUIFlag', { isDeletingThread: true });
    try {
      await InternalChatAPI.deleteThread(threadId);
      commit('removeThread', threadId);
    } finally {
      commit('setUIFlag', { isDeletingThread: false });
    }
  },
  onThreadUpdated: ({ commit }, { thread }) => {
    commit('upsertThread', thread);
  },
  onMessageCreated: ({ commit }, { thread, message }) => {
    commit('upsertThread', thread);
    commit('appendMessage', { threadId: thread.id, message });
  },
  onMessageUpdated: ({ commit }, { thread, message }) => {
    commit('upsertThread', thread);
    commit('upsertMessage', { threadId: thread.id, message });
  },
  onThreadDeleted: ({ commit }, { thread_id: threadId }) => {
    commit('removeThread', threadId);
  },
};

export const mutations = {
  setUIFlag($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  setThreads($state, threads) {
    $state.threads = sortThreads(threads || []);
  },
  upsertThread($state, thread) {
    upsertThread($state.threads, thread);
  },
  removeThread($state, threadId) {
    const normalizedThreadId = Number(threadId);
    $state.threads = $state.threads.filter(
      thread => thread.id !== normalizedThreadId
    );
    const { [normalizedThreadId]: _removedMessages, ...messages } =
      $state.messages;
    $state.messages = messages;
  },
  setMessages($state, { threadId, messages }) {
    $state.messages = {
      ...$state.messages,
      [threadId]: messages || [],
    };
  },
  prependMessages($state, { threadId, messages }) {
    const currentMessages = $state.messages[threadId] || [];
    $state.messages = {
      ...$state.messages,
      [threadId]: [...(messages || []), ...currentMessages],
    };
  },
  appendMessage($state, { threadId, message }) {
    const currentMessages = $state.messages[threadId] || [];
    if (currentMessages.some(item => item.id === message.id)) return;

    $state.messages = {
      ...$state.messages,
      [threadId]: [...currentMessages, message],
    };
  },
  upsertMessage($state, { threadId, message }) {
    const currentMessages = $state.messages[threadId] || [];
    const existingIndex = currentMessages.findIndex(
      item => item.id === message.id
    );
    const messages =
      existingIndex === -1
        ? [...currentMessages, message]
        : currentMessages.map(item => {
            if (item.id === message.id) return message;
            if (item.reply_to?.id === message.id) {
              return { ...item, reply_to: message };
            }

            return item;
          });

    $state.messages = {
      ...$state.messages,
      [threadId]: messages,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
